import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { theme } from '../styled/theme';
import { Container, GradientTitle, Card, Flex, ButtonLink, TextMuted } from '../styled/BaseComponents';
import { fetchTradingPair, getVotingGeneral } from '../../../../utils/services/user.service';

const TradingWrapper = styled.section`
  margin-top: 1.5rem;
  display: grid;
  gap: 16px;
  grid-template-columns: 1.2fr 1fr;

  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

const TradingSectionWrapper = styled.div`
  margin-top: 1.5rem;
`;

const TradingSection = styled.div``;

const TradingCard = styled(Card)`
  padding: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const TradingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${theme.colors.border};
  padding: 8px;
  gap: 8px;
  flex-wrap: wrap;
  color: ${theme.colors.text};

  &:last-child {
    border-bottom: none;
  }
`;

const TradingInfo = styled.div`
  color: ${theme.colors.text};
`;

const TradingSymbol = styled.strong`
  color: ${theme.colors.text};
`;

const TradingPrice = styled.span`
  color: ${theme.colors.text};
`;

const TradingChange = styled.div`
  font-weight: 900;
  color: ${({ down }) => (down ? theme.colors.error : theme.colors.success)};
`;

const TradingActions = styled(Flex)`
  gap: 0.5rem;
`;

const SmallButton = styled(ButtonLink)`
  font-size: 12px;
`;

const VotingSection = styled.div``;

const VotingCard = styled(Card)`
  padding: 1rem;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const VotingHeader = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
  color: ${theme.colors.text};
`;

const VotingTitle = styled.strong`
  color: ${theme.colors.text};
`;

const VotingTime = styled.div`
  color: ${theme.colors.muted};
  font-size: 12px;
`;

const VotingOption = styled.div`
  margin-top: 0.5rem;
  color: ${theme.colors.text};
`;

const VotingBar = styled.div`
  height: 8px;
  background: #0b1324;
  border: 1px solid ${theme.colors.border};
  border-radius: 999px;
  overflow: hidden;
  margin-top: 4px;
`;

const VotingBarFill = styled.div`
  height: 100%;
  width: ${({ percentage }) => percentage}%;
  background: ${({ color }) => color};
`;

const VotingPercentage = styled(TextMuted)`
  font-size: 12px;
`;

const SectionTitle = styled(GradientTitle)`
  padding-left: 8px;
  margin: 8px 0;
`;

const TradingRowComponent = ({ pair }) => {
  const symbol = pair?.trading_pair || '—';
  const priceValue = pair?.current_price?.[0]?.price;
  const formattedPrice = priceValue !== undefined && priceValue !== null ? `${priceValue}` : '—';
  const diff = pair?.diffPercent;
  const hasDiff = typeof diff === 'number' && !Number.isNaN(diff);
  const formattedDiff = hasDiff ? `${diff > 0 ? '+' : ''}${diff.toFixed(2)}%` : '—';
  const down = hasDiff ? diff < 0 : false;

  return (
    <TradingRow>
      <TradingInfo>
        <TradingSymbol>{symbol}</TradingSymbol>
        <TradingPrice> · ${formattedPrice}</TradingPrice>
      </TradingInfo>
      <TradingChange down={down}>{formattedDiff}</TradingChange>
      <TradingActions>
        <SmallButton to="/sign-in">Buy</SmallButton>
        <SmallButton to="/sign-in">Sell</SmallButton>
      </TradingActions>
    </TradingRow>
  );
};

const VotingCardComponent = ({ voting }) => {
  const calcRemainingSeconds = (endDate) => {
    if (!endDate) return 0;
    const end = new Date(endDate).getTime();
    return Math.max(0, Math.floor((end - Date.now()) / 1000));
  };

  const [remain, setRemain] = useState(calcRemainingSeconds(voting?.end_date));

  useEffect(() => {
    const tick = () => {
      setRemain(calcRemainingSeconds(voting?.end_date));
    };

    tick();
    const id = setInterval(() => {
      const remaining = calcRemainingSeconds(voting?.end_date);
      setRemain(remaining);
      if (remaining <= 0) {
        clearInterval(id);
      }
    }, 1000);

    return () => clearInterval(id);
  }, [voting?.end_date]);

  const d = Math.floor(remain / 86400);
  const h = Math.floor((remain % 86400) / 3600);
  const m = Math.floor((remain % 3600) / 60);
  const s = remain % 60;

  const optionsCount = Math.max(voting?.option || 0, voting?.option_name?.length || 0);
  const votes = Array.from({ length: optionsCount }, (_, idx) => Number(voting?.option_name?.[idx]) || 0);
  const totalVotes = votes.reduce((sum, val) => sum + val, 0);
  const colors = ['#5ce9ff', '#fb7185', '#a855f7', '#22d3ee'];

  return (
    <VotingCard>
      <VotingHeader>
        <VotingTitle>{voting?.title || 'Voting Event'}</VotingTitle>
        <VotingTime>
          {String(d).padStart(2, '0')}d : {String(h).padStart(2, '0')}h : {String(m).padStart(2, '0')}m : {String(s).padStart(2, '0')}s
        </VotingTime>
      </VotingHeader>
      {votes.length === 0 ? (
        <TextMuted>No voting options available.</TextMuted>
      ) : (
        votes.map((count, idx) => {
          const percentage = totalVotes > 0 ? (count / totalVotes) * 100 : 0;
          const color = colors[idx % colors.length];
          const label = idx === 0 ? 'Yes' : idx === 1 ? 'No' : `Option ${idx + 1}`;

          return (
            <React.Fragment key={`${voting?._id || 'option'}-${idx}`}>
              <VotingOption>{label} — {voting?.description || ''}</VotingOption>
              <VotingBar>
                <VotingBarFill percentage={percentage} color={color} />
              </VotingBar>
              <VotingPercentage>
                {percentage.toFixed(0)}% ({count} vote{count === 1 ? '' : 's'})
              </VotingPercentage>
            </React.Fragment>
          );
        })
      )}
    </VotingCard>
  );
};

export const TradingVoting = () => {
  const [tradingPairs, setTradingPairs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [votingGeneral, setVotingData] = useState([]);
  console.log('votingGeneral', votingGeneral);
  const [isVotingLoading, setIsVotingLoading] = useState(false);

  useEffect(() => {
    const fetchTradingPairs = async () => {
      setIsLoading(true);
      try {
        const response = await fetchTradingPair();
        setTradingPairs(response?.data?.data || []);
      } catch (error) {
        console.error('Error fetching trading pairs:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTradingPairs();
  }, []);

  useEffect(() => {
    const fetchVotingGeneralData = async () => {
      setIsVotingLoading(true);
      try {
        const response = await getVotingGeneral();
        setVotingData(response?.data?.data || []);
      } catch (error) {
        console.error('Error fetching voting general:', error);
      } finally {
        setIsVotingLoading(false);
      }
    };

    fetchVotingGeneralData();
  }, []);


  return (
    <TradingSectionWrapper>
      <Container>
        <TradingWrapper>
          <TradingSection>
            <SectionTitle>Trading Section</SectionTitle>
            <TradingCard>
              {tradingPairs.length > 0 ? (
                tradingPairs.slice(0, 6).map((pair) => <TradingRowComponent key={pair._id || pair.trading_pair} pair={pair} />)
              ) : (
                <TextMuted style={{ padding: '16px' }}>{isLoading ? 'Loading trading pairs…' : 'No trading pairs available.'}</TextMuted>
              )}
            </TradingCard>
          </TradingSection>
          <VotingSection>
            <SectionTitle>Voting Section</SectionTitle>
            {votingGeneral.length > 0 ? (
              votingGeneral.slice(0, 1).map((event) => <VotingCardComponent key={event._id} voting={event} />)
            ) : (
              <VotingCard>
                <TextMuted>{isVotingLoading ? 'Loading voting events…' : 'No voting events available.'}</TextMuted>
              </VotingCard>
            )}
          </VotingSection>
        </TradingWrapper>
      </Container>
    </TradingSectionWrapper>
  );
};

export default TradingVoting;

